<script setup lang="ts">
import type { BlobObject } from '../../types/gallery'
import { decodeImageSlug, encodeImageSlug, imageUrl } from '../utils/url.ts'

const imageEl = ref<HTMLImageElement>()
const magnifierEl = ref<HTMLElement>()
const imageContainer = ref<HTMLElement>()
const savingImg = ref(false)
const showNavControls = ref(false)
const thumbnailBarCollapsed = useState<boolean>('detail-thumbnail-bar-collapsed', () => false)
const thumbnailBarExpandedHeight = 68
const thumbnailBarCollapsedHeight = 10
let navControlsHideTimer: ReturnType<typeof setTimeout> | undefined

// filter
const filter = ref(false)
const contrast = ref(100)
const blur = ref(0)
const hueRotate = ref(0)
const invert = ref(0)
const saturate = ref(100)
const sepia = ref(0)
const magnifier = ref(false)
const zoomFactor = ref(1)
const objectsFit = ref(['Contain', 'Cover', 'Scale-down', 'Fill', 'None'])
const objectFitSelected = ref(objectsFit.value[0])
const filterUpdated = ref(false)

const { images, uploadImage } = useFile()
const { currentIndex, navigableImages, isFirstImg, isLastImg, _downloadImage, applyFilters, initSwipe, convertBase64ToFile, magnifierImage } = useImageGallery()

const active = useState()
const route = useRoute()
const router = useRouter()
const topicPathSegments = computed(() => {
  const topicPathParam = route.params.path
  return (Array.isArray(topicPathParam) ? topicPathParam : topicPathParam ? [topicPathParam] : [])
    .flatMap(segment => segment.split('/'))
    .map(segment => segment.trim())
    .filter(Boolean)
})

const galleriesIndex = computed(() => topicPathSegments.value.indexOf('galleries'))
const detailIndex = computed(() => topicPathSegments.value.indexOf('detail'))

const galleryName = computed(() => {
  const index = galleriesIndex.value
  return index >= 0 ? (topicPathSegments.value[index + 1] || '') : ''
})

const detailSlug = computed(() => {
  const index = detailIndex.value
  return index >= 0 ? (topicPathSegments.value[index + 1] || '') : ''
})

const galleryBasePath = computed(() => {
  const index = galleriesIndex.value

  if (index >= 0 && galleryName.value) {
    const topicSegments = topicPathSegments.value.slice(0, index)
    return `/topics/${topicSegments.join('/')}/galleries/${galleryName.value}`
  }

  return ''
})

const detailTopOffset = computed(() => thumbnailBarCollapsed.value ? thumbnailBarCollapsedHeight : thumbnailBarExpandedHeight)
const detailViewportStyle = computed(() => ({
  marginTop: `${detailTopOffset.value}px`,
  height: `calc(100dvh - ${detailTopOffset.value}px)`
}))

const image: ComputedRef<BlobObject> = computed(() => {
  if (!detailSlug.value || !images.value) {
    return {} as BlobObject
  }

  const decodedSlug = decodeImageSlug(detailSlug.value)
  const foundImage = images.value.find((file: BlobObject) => file.pathname === decodedSlug || file.pathname.endsWith(`/${decodedSlug}`))

  if (!foundImage) {
    console.warn(`Image not found for slug: ${decodedSlug}`)
    return {} as BlobObject
  }

  return foundImage
})

const imageMetaLine = computed(() => {
  const meta = image.value?.meta
  if (!meta) {
    return ''
  }

  return [meta.cameraModel, meta.aperture, meta.focalLength, meta.exposureTime]
    .filter(Boolean)
    .join(' · ')
})

const hasImageMeta = computed(() => {
  const meta = image.value?.meta
  if (!meta) {
    return false
  }

  return !!(meta.title || meta.description || imageMetaLine.value)
})

function clearNavControlsHideTimer() {
  if (navControlsHideTimer) {
    clearTimeout(navControlsHideTimer)
    navControlsHideTimer = undefined
  }
}

const NAV_CONTROLS_HIDE_TIMEOUT = 2000

function scheduleNavControlsHide() {
  clearNavControlsHideTimer()
  navControlsHideTimer = setTimeout(() => {
    showNavControls.value = false
  }, NAV_CONTROLS_HIDE_TIMEOUT)
}

function handleImageHoverMove() {
  showNavControls.value = true
  scheduleNavControlsHide()
}

function handleImageHoverEnd() {
  clearNavControlsHideTimer()
  showNavControls.value = false
}

function toggleThumbnailBar() {
  thumbnailBarCollapsed.value = !thumbnailBarCollapsed.value
}

onKeyStroke('Escape', () => {
  router.push(galleryBasePath.value)
})

onKeyStroke('ArrowLeft', () => {
  if (isFirstImg.value)
    router.push(galleryBasePath.value)
  else
    router.push(`${galleryBasePath.value}/detail/${encodeImageSlug(navigableImages.value[currentIndex.value - 1]?.pathname || '')}`)
})

onKeyStroke('ArrowRight', () => {
  if (isLastImg.value)
    router.push(galleryBasePath.value)
  else
    router.push(`${galleryBasePath.value}/detail/${encodeImageSlug(navigableImages.value[currentIndex.value + 1]?.pathname || '')}`)
})

function resetFilter() {
  contrast.value = 100
  blur.value = 0
  invert.value = 0
  saturate.value = 100
  hueRotate.value = 0
  sepia.value = 0
  filterUpdated.value = false
  magnifier.value = false
  zoomFactor.value = 1
}

function _cancelFilter() {
  filter.value = false

  resetFilter()
}

async function _saveImage() {
  if (filterUpdated.value && imageEl.value) {
    savingImg.value = true

    const modifiedImage = await applyFilters(imageEl.value, contrast.value, blur.value, invert.value, saturate.value, hueRotate.value, sepia.value)

    const imageToUpload = await convertBase64ToFile(modifiedImage, image)

    await uploadImage(imageToUpload, galleryName.value, galleryBasePath.value).finally(() => savingImg.value = false)
  }
}

watch([contrast, blur, invert, saturate, hueRotate, sepia], () => {
  filterUpdated.value = true
})

onMounted(() => {
  initSwipe(imageEl)
  showNavControls.value = true
  scheduleNavControlsHide()
})

onBeforeUnmount(() => {
  clearNavControlsHideTimer()
})
</script>

<template>
  <div v-if="image">
    <!-- background -->
    <div class="absolute inset-0 w-full h-full">
      <img
        :src="imageUrl(image.pathname)"
        class="object-cover w-full h-full blur-[70px] brightness-[.2] will-change-[filter]"
        alt=""
      >
    </div>

    <div class="relative overflow-hidden h-[100dvh] w-full">
      <ImageFilters
        class="absolute top-[84px] z-[70] transition-transform duration-200"
        :class="filter ? 'translate-x-0 right-4 md:right-8' : 'translate-x-full right-0'"
        @reset-filter="resetFilter"
        @close-filter="filter = false"
      >
        <div
          class="flex flex-col gap-y-12 pb-6 h-[60dvh]"
          :class="filter ? 'block opacity-100' : 'hidden opacity-0'"
        >
          <div class="flex flex-col gap-y-4">
            <!-- filters list -->
            <div class="flex gap-x-4 justify-between items-center pb-4">
              <span class="text-white w-40">Fit</span>
              <USelectMenu
                v-model="objectFitSelected"
                :items="objectsFit"
                class="!w-52 mr-4"
              />
            </div>

            <div class="flex gap-x-4 w-full justify-end pr-4 pb-4">
              <UCheckbox
                v-model="magnifier"
                name="magnifier"
                label="Magnifier"
                color="gray"
                :ui="{ label: 'text-gray-300 dark:text-gray-300' }"
              />
              <UIcon
                name="i-heroicons-magnifying-glass-solid"
                class="w-5 h-5 text-gray-300"
              />
            </div>

            <UGauge
              v-if="magnifier"
              v-model="zoomFactor"
              :max="4"
              title="Zoom level"
            />
            <UGauge
              v-model="sepia"
              :max="100"
              title="Sepia"
            />
            <UGauge
              v-model="hueRotate"
              :max="180"
              title="Hue-rotate"
            />
            <UGauge
              v-model="saturate"
              :max="100"
              title="Saturate"
            />
            <UGauge
              v-model="invert"
              :max="100"
              title="Invert"
            />
            <UGauge
              v-model="contrast"
              :max="200"
              title="Contrast"
            />
            <UGauge
              v-model="blur"
              :max="5"
              title="Blur"
            />
          </div>
        </div>
      </ImageFilters>

      <div
        class="absolute left-0 right-0 z-30 border-t border-white/15"
        :style="{ top: `${detailTopOffset}px` }"
      >
        <div class="flex justify-center -translate-y-1">
          <UButton
            size="xs"
            color="neutral"
            variant="soft"
            :icon="thumbnailBarCollapsed ? 'i-heroicons-chevron-down-16-solid' : 'i-heroicons-chevron-up-16-solid'"
            :aria-label="thumbnailBarCollapsed ? 'Show thumbnails' : 'Hide thumbnails'"
            @click="toggleThumbnailBar"
          />
        </div>
      </div>

      <div
        class="relative z-10 w-full"
        :style="detailViewportStyle"
      >
        <div
          :class="filter ? 'lg:pr-[360px]' : ''"
          class="transition-all duration-200 h-full w-full relative"
        >
          <div
            class="relative h-full w-full"
            @mousemove="handleImageHoverMove"
            @mouseleave="handleImageHoverEnd"
          >
            <div class="relative h-full w-full flex items-center justify-center px-2 md:px-8 py-4 md:py-8">
              <!-- image -->
              <div
                ref="imageContainer"
                class="group relative flex h-full w-full items-center justify-center"
              >
                <img
                  v-if="image"
                  ref="imageEl"
                  :src="imageUrl(image.pathname)"
                  :alt="image.pathname"
                  class="imageEl rounded object-contain transition-all duration-200 block max-h-full max-w-full h-auto w-auto"
                  :class="filter ? 'max-w-[88%]' : ''"
                  :style="`filter: contrast(${contrast}%) blur(${blur}px) invert(${invert}%) saturate(${saturate}%) hue-rotate(${hueRotate}deg) sepia(${sepia}%); object-fit:${(objectFitSelected ?? 'Contain').toLowerCase()}; max-height: 100%; max-width: 100%;`"
                  crossorigin="anonymous"
                  @mousemove="magnifier ? magnifierImage($event, imageContainer!, imageEl!, magnifierEl!, zoomFactor) : () => {}"
                >
                <div
                  v-if="magnifier"
                  ref="magnifierEl"
                  class="w-[100px] h-[100px] absolute border border-gray-200 pointer-events-none rounded-full block opacity-0 group-hover:opacity-100 transition-opacity duration-200 "
                  :style="`background-image: url('${imageUrl(image.pathname)}')`"
                />
              </div>
            </div>

            <div
              class="absolute inset-y-0 left-2 md:left-4 z-20 hidden md:flex items-center transition-opacity duration-200"
              :class="showNavControls ? 'opacity-100' : 'opacity-0 pointer-events-none'"
            >
              <UTooltip
                v-if="!isFirstImg"
                text="Previous"
                :shortcuts="['←']"
              >
                <UButton
                  variant="ghost"
                  color="gray"
                  :to="navigableImages && navigableImages[currentIndex - 1] ? `${galleryBasePath}/detail/${encodeImageSlug(navigableImages[currentIndex - 1]?.pathname || '')}` : galleryBasePath"
                  size="lg"
                  icon="i-heroicons-chevron-left"
                  aria-label="Go to previous image"
                  @click="active = image.pathname.split('/').pop()?.split('.')[0]"
                />
              </UTooltip>
            </div>

            <div
              class="absolute inset-y-0 right-2 md:right-4 z-20 hidden md:flex items-center transition-opacity duration-200"
              :class="showNavControls ? 'opacity-100' : 'opacity-0 pointer-events-none'"
            >
              <UTooltip
                v-if="!isLastImg"
                text="Next"
                :shortcuts="['→']"
              >
                <UButton
                  variant="ghost"
                  color="gray"
                  :to="navigableImages && navigableImages[currentIndex + 1]?.pathname ? `${galleryBasePath}/detail/${encodeImageSlug(navigableImages[currentIndex + 1]?.pathname || '')}` : galleryBasePath"
                  size="lg"
                  icon="i-heroicons-chevron-right"
                  class="rounded-full"
                  aria-label="Go to next image"
                  @click="active = image.pathname.split('/').pop()?.split('.')[0]"
                />
              </UTooltip>
            </div>

            <div
              class="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 hidden md:flex items-center transition-opacity duration-200"
              :class="showNavControls ? 'opacity-100' : 'opacity-0 pointer-events-none'"
            >
              <UTooltip
                text="Back to gallery"
                :shortcuts="['Esc']"
              >
                <UButton
                  variant="ghost"
                  color="gray"
                  :to="galleryBasePath"
                  size="xl"
                  class="back transition-colors duration-200"
                  aria-label="Back"
                  @click="active = image.pathname.split('/').pop()?.split('.')[0]"
                >
                  <UIcon
                    name="i-heroicons-arrow-uturn-left-solid"
                    class="w-5 h-5"
                  />Back to gallery
                </UButton>
              </UTooltip>
            </div>

            <div
              v-if="hasImageMeta"
              class="absolute left-4 md:left-6 bottom-4 z-20 max-w-[min(90vw,560px)] rounded-lg border border-white/15 bg-black/55 p-3 text-xs text-gray-200 transition-opacity duration-200"
              :class="showNavControls ? 'opacity-100' : 'opacity-0 pointer-events-none'"
            >
              <p
                v-if="image.meta?.title"
                class="text-white font-medium"
              >
                {{ image.meta.title }}
              </p>
              <p
                v-if="image.meta?.description"
                class="mt-1"
              >
                {{ image.meta.description }}
              </p>
              <p
                v-if="imageMetaLine"
                class="mt-1 text-[11px] text-gray-300"
              >
                {{ imageMetaLine }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
@media (min-width: 768px) {

  .bottom-menu {
    view-transition-name: vtn-bottom-menu;
  }

  .bottom-menu-description {
    view-transition-name: vtn-bottom-menu-description;
  }

  .bottom-menu-button {
    view-transition-name: vtn-bottom-menu-button;
  }

  .back {
    view-transition-name: vtn-back-button;
  }
}
</style>

<style>
@keyframes slide-to-left {
  to {
    transform: translateX(0px);
  }
}

::view-transition-old(vtn-bottom-menu-description) {
  animation: 300ms cubic-bezier(0.4, 0, 0.2, 1) both slide-to-left;
  width: auto;
  opacity: 0;
}
</style>
